import { useRef, useState } from "react";
import { Clock3, FileText, History, Pencil } from "lucide-react";
import { cn } from "@/utils/helpers";
import type { MarketSignal } from "@/types/marketSignal.type";
import type { FieldReport } from "@/types/fieldReport.type";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { acceptWindow, formatShortDate, kindLabelSingular } from "./signalMeta";
import AcceptSplit, { type AcceptChoice } from "./AcceptWindowMenu";
import EditPendingSignal, { type PendingSignalEdit } from "./EditPendingSignal";
import EvidenceColumn from "./EvidenceColumn";
import KindGlyph from "./KindGlyph";

export type { PendingSignalEdit };

/**
 * A pending signal awaiting the curator's call: one square card, the READ on the
 * left and the EVIDENCE it was grounded in on the right.
 *
 * Accept is a split capsule: its left half sends acceptWindow(signal).validUntil,
 * the window the sentence above it names; its arrow offers the other windows and
 * Permanent (AcceptWindowMenu). A suggestion that has already passed is never
 * sent; it falls back to the kind default, and the card says so before anyone
 * clicks. Decline maps to dismiss. Evidence inspection stays observations-only.
 *
 * When the field report the signal was filed from is known (same run), the
 * proposed date becomes a link to it: the reader can read the whole market read
 * behind a signal before deciding on it.
 */
export default function PendingSignalCard({
  signal,
  onAccept,
  onDecline,
  onEdit,
  onOpenSources,
  report = null,
  onOpenReport,
  busy = false,
  settling = false,
}: {
  signal: MarketSignal;
  onAccept?: (choice: AcceptChoice) => void;
  onDecline?: () => void;
  /** Persist an inline edit through the parent's updateSignal mutation. */
  onEdit?: (patch: PendingSignalEdit) => void;
  /** Open the sources dialog, on one source when an id is given. */
  onOpenSources: (observationId?: string) => void;
  /** The field report this signal was filed from (signal.sourceRunId === report.runId). */
  report?: FieldReport | null;
  onOpenReport?: () => void;
  busy?: boolean;
  /** The card is leaving the queue (optimistic triage): it no longer takes input. */
  settling?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  const isEditing = editing && !!onEdit;

  const win = acceptWindow(signal);
  const conf = signal.confidence;
  const hasConfidence = conf != null && Number.isFinite(conf);
  const titleId = `sig-title-${signal.id}`;
  const locked = busy || settling;

  /* Editing swaps the controls the user was on for a form, and closing it swaps
     them back, so focus is carried across both swaps rather than dropped. */
  const focusSoon = (pick: (card: HTMLElement) => HTMLElement | null) =>
    requestAnimationFrame(() => {
      const card = cardRef.current;
      if (card) (pick(card) ?? card).focus();
    });
  const startEdit = () => {
    setEditing(true);
    focusSoon((card) => card.querySelector<HTMLElement>(".sig-edit [tabindex='0'], .sig-edit input"));
  };
  const stopEdit = () => {
    setEditing(false);
    focusSoon(() => null);
  };

  return (
    <article
      ref={cardRef}
      className={cn("sig-card", isEditing && "is-editing")}
      aria-labelledby={titleId}
      tabIndex={-1}
      inert={settling || undefined}
    >
      <div className="sig-card__read">
        {isEditing && onEdit ? (
          <>
            <h3 className="sr-only" id={titleId}>
              Editing: {signal.title}
            </h3>
            <EditPendingSignal
              signal={signal}
              busy={busy}
              onSave={(patch) => {
                onEdit(patch);
                stopEdit();
              }}
              onCancel={stopEdit}
            />
          </>
        ) : (
          <>
            <div className="sig-card__meta">
              <span className="sig-card__kind">
                <KindGlyph kind={signal.kind} size={15} className="sig-glyph" />
                <span className="sig-kind-label">{kindLabelSingular(signal.kind)}</span>
                <span className="sig-dot" aria-hidden="true" />
                {report && onOpenReport ? (
                  <button type="button" className="sig-report-link" onClick={onOpenReport}>
                    <FileText aria-hidden="true" />
                    From the field report of {formatShortDate(report.createdAt)}
                  </button>
                ) : (
                  <span className="sig-card__proposed">
                    Proposed {formatShortDate(signal.createdAt)}
                  </span>
                )}
              </span>
              {hasConfidence ? (
                <span className="sig-card__conf">
                  <span className="sig-card__conf-n">{Math.round((conf ?? 0) * 100)}%</span>
                  <span className="sig-card__conf-l">Confidence</span>
                </span>
              ) : signal.sourceRunId === null ? (
                <span className="sig-card__conf-l">Logged by hand</span>
              ) : null}
            </div>

            <h3 className="sig-card__title" id={titleId} dir="auto">
              {signal.title}
            </h3>
            <p className="sig-card__body" dir="auto">
              {signal.body}
            </p>

            <p className={cn("sig-card__window", win.stale && "is-stale")}>
              {win.stale ? <History aria-hidden="true" /> : <Clock3 aria-hidden="true" />}
              <span>{win.sentence}</span>
            </p>
          </>
        )}
      </div>

      {!isEditing && (onAccept || onDecline || onEdit) ? (
        <div className="sig-card__acts">
          {onAccept ? (
            <AcceptSplit signal={signal} isDisabled={locked} onAccept={onAccept} />
          ) : null}
          {onDecline ? (
            <KitButton
              variant="secondary"
              className="sig-btn sig-btn--decline"
              isDisabled={locked}
              aria-describedby={titleId}
              onClick={() => onDecline()}
            >
              Decline
            </KitButton>
          ) : null}
          {onEdit ? (
            <button
              type="button"
              className="sig-text-btn"
              disabled={locked}
              aria-describedby={titleId}
              onClick={startEdit}
            >
              <Pencil aria-hidden="true" />
              Edit
            </button>
          ) : null}
        </div>
      ) : null}

      {isEditing ? null : (
        <div className="sig-card__ev">
          <EvidenceColumn signal={signal} onOpenSources={onOpenSources} />
        </div>
      )}
    </article>
  );
}
